import React, { useEffect, useState } from "react";
import "../Styles/BrowseAgents.css";
import { FaMapMarkerAlt } from "react-icons/fa";
import { IoIosArrowForward } from "react-icons/io";
import Logo from "../assests/images/agent-login-signup-top-logo.png"; // Adjust path if needed
import Select from "react-select";
import SelectedPartnerDetails from "./SelectedPartnerDetails";
import LangIcon from "../assests/images/lang-icon.png";
import {
  get_all_agents,
  get_all_castes,
  get_castes,
  get_communities,
  get_language,
  get_religios,
} from "../api/serverWrapper";
import { selectStyles } from "../utils/commonFunctions";
import { useMasterData } from "../context/MasterDataContext";
import { useNavigate, useLocation } from "react-router-dom";

const RatingStars = ({ rating = 0 }) => {
  const numericRating = Number(rating) || 0;

  const getStarFill = (starIndex) => {
    // starIndex is 1-based (1st star, 2nd star, ...)
    const diff = numericRating - (starIndex - 1);
    if (diff >= 1) return 100; // fully filled
    if (diff <= 0) return 0; // empty
    return diff * 100; // partial (e.g. 0.3 -> 30%)
  };

  return (
    <div
      className="agent-browse-rating-stars"
      style={{ height: 10, display: "flex" }}
    >
      {numericRating > 0 &&
        [1, 2, 3, 4, 5].map((starIndex) => (
          <span
            key={starIndex}
            style={{
              position: "relative",
              display: "inline-block",
              fontSize: 14,
              lineHeight: 1,
            }}
          >
            {/* empty star (background) */}
            <span style={{ color: "#d0d0d0" }}>★</span>

            {/* filled star (clipped by width %) */}
            <span
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                overflow: "hidden",
                width: `${getStarFill(starIndex)}%`,
                color: "#f5b301",
                whiteSpace: "nowrap",
              }}
            >
              ★
            </span>
          </span>
        ))}
    </div>
  );
};

function BrowseAgents() {
  const navigate = useNavigate();

  const {
    religionMasterData,
    allCasteMasterData,
    communityMasterData,
    languageMasterData,
    allCountyMasterData,
  } = useMasterData();

  const location = useLocation();

  const fromHome = location.state?.fromHome;

  const [showSearchBox, setShowSearchBox] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [religion, setReligion] = useState([]);
  const [caste, setCaste] = useState([]);
  const [community, setCommunity] = useState([]);
  const [allAgents, setAllAgents] = useState([]);
  const [religionOptions, setReligionOptions] = useState([]);
  const [casteOptions, setCasteOptions] = useState([]);
  const [communityOptions, setCommunityOptions] = useState([]);
  const [languages, setLanguage] = useState(languageMasterData || []);

  // 👇 new states for location
  const [currentCity, setCurrentCity] = useState(null);
  const [currentState, setCurrentState] = useState(null);

  useEffect(() => {
    loadData();
    detectLocation();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      let resp = await get_all_agents();

      setAllAgents(resp.data);

      const religionData = religionMasterData?.map((item) => ({
        value: item.ReligionId,
        label: item.ReligionName,
      }));
      setReligionOptions(religionData);

      const casteData = allCasteMasterData?.map((item) => ({
        value: item.CasteL2Id,
        label: item.CasteName,
      }));
      setCasteOptions(casteData);

      const communitieData = communityMasterData?.map((item) => ({
        value: item.CommunitiesId,
        label: item.CommunitiesName,
      }));
      setCommunityOptions(communitieData);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const detectLocation = () => {
    if (!navigator.geolocation) {
      console.warn("Geolocation is not supported by this browser.");
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        console.log(lat);
        console.log(lon);

        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`,
            {
              headers: {
                "User-Agent": "WedLukApp/1.0",
                "Accept-Language": "en",
              },
            },
          );

          if (!res.ok) throw new Error("Failed to fetch location");

          const data = await res.json();
          const city =
            data.address.city ||
            data.address.town ||
            data.address.village ||
            null;
          let state = data.address.state || null;
          if (state) state = state.substring(0, 2).toUpperCase();

          setCurrentCity(city);
          setCurrentState(state);

          console.log("Detected location:", city, state);
        } catch (err) {
          console.error("Error fetching location data:", err);
        }
      },
      (err) => {
        // Handle permission denied or other errors
        if (err.code === 1) {
          alert(
            "Location access denied. Please enable location to see nearby agents.",
          );
        } else {
          console.error("Geolocation error:", err);
        }
      },
    );
  };

  const sortedAgents = [...allAgents]
    .filter((partner) => {
      // Search filter
      const matchesSearch = partner.BusinessName.toLowerCase().includes(
        searchTerm.toLowerCase(),
      );

      // Religion filter
      const matchesReligion =
        religion.length === 0 ||
        religion.some((r) => partner.ReligionsServe.includes(r.value));

      // Caste filter
      const matchesCaste =
        caste.length === 0 ||
        caste.some((c) => partner.CastesServe.includes(c.value));

      // Community filter
      const matchesCommunity =
        community.length === 0 ||
        community.some((com) => partner.CommunitesServe.includes(com.value));

      return (
        matchesSearch && matchesReligion && matchesCaste && matchesCommunity
      );
    })
    .sort((a, b) => {
      if (!currentState) return 0;

      const aState = a?.User?.Address?.StateId?.toUpperCase();
      const bState = b?.User?.Address?.StateId?.toUpperCase();

      // 🔥 User state comes first
      if (aState === currentState && bState !== currentState) return -1;
      if (aState !== currentState && bState === currentState) return 1;

      return 0;
    });

  return (
    <div className="">
      {/* Header */}
      <header className="agent-browse-header agent-browse-header-fixed ">
        <div className="">
          <img src={Logo} alt="WedLuk Logo" className="agent-browse-logo" />
          <h1 className="agent-browse-title">Our registered partners</h1>
        </div>
        <div
          className="agent-browse-title-name"
          onClick={() => navigate("/signup")}
        >
          {selectedPartner && selectedPartner.BusinessName}
          Join Us {">"}
        </div>
      </header>
      <div className="agent-browse-scroll-wrapper">
        {!selectedPartner && (
          <div className="agent-browse-container">
            {/* Filters */}
            <div className="agent-browse-multiselect-wrapper">
              <div className="agent-browse-multiselect-block">
                <label>Religions they serve</label>
                <Select
                  isMulti
                  options={religionOptions}
                  value={religion}
                  onChange={setReligion}
                  classNamePrefix="agent-browse-select"
                  placeholder="Select religions"
                  styles={selectStyles}
                />
              </div>

              <div className="agent-browse-multiselect-block">
                <label>Castes they serve</label>
                <Select
                  isMulti
                  options={casteOptions}
                  value={caste}
                  onChange={setCaste}
                  classNamePrefix="agent-browse-select"
                  placeholder="Select castes"
                  styles={selectStyles}
                />
              </div>

              <div className="agent-browse-multiselect-block">
                <label>Communities they serve</label>
                <Select
                  isMulti
                  options={communityOptions}
                  value={community}
                  onChange={setCommunity}
                  classNamePrefix="agent-browse-select"
                  placeholder="Select communities"
                  styles={selectStyles}
                />
              </div>
              <img
                className="search-agents-list click-effect"
                src={require("../assests/images/search-clients.png")}
                alt=""
                onClick={() => setShowSearchBox(!showSearchBox)}
                style={{ cursor: "pointer" }}
              />
            </div>

            {showSearchBox && (
              <div className="small-search-box">
                <input
                  type="text"
                  placeholder="Search Business Name"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button
                  className="small-search-close"
                  onClick={() => setShowSearchBox(false)}
                >
                  ×
                </button>
              </div>
            )}

            {/* Cards */}
            <div className="agent-browse-card-grid">
              {loading && (
                <div className="agent-loading-container">
                  <div className="agent-loading-spinner"></div>
                  <p>Loading agents...</p>
                </div>
              )}
              {!loading &&
                sortedAgents.map((partner, idx) => (
                  <div
                    key={idx}
                    className="agent-browse-card click-effect"
                    onClick={() =>
                      navigate("/agent-details", {
                        state: {
                          partner,
                          religionOptions,
                          casteOptions,
                          communityOptions,
                          languages,
                        },
                      })
                    }
                  >
                    <div className="agent-browse-card-header">
                      <div className="agent-browse-name">
                        {partner.BusinessName}
                      </div>
                      <IoIosArrowForward className="agent-browse-arrow-icon" />
                    </div>

                    <RatingStars rating={Number(partner.AverageRating || 0)} />

                    <div className="d-flex align-items-center justify-content-between">
                      <div className="agent-browse-info-row">
                        <FaMapMarkerAlt className="agent-browse-icon" />
                        <span>
                          {partner?.User?.Address?.CityId ?? "NA"}{" "}
                          {partner?.User?.Address?.StateId ?? "NA"}{" "}
                          {partner?.User?.Address?.CountryId ?? "NA"}
                        </span>
                      </div>

                      <div className="agent-browse-info-row">
                        <img
                          className="icons-for-partner"
                          src={LangIcon}
                          alt=""
                        />
                        {(() => {
                          // ✅ If ALL languages selected
                          if (partner?.LanguagesServe?.includes("all")) {
                            return <span>All Languages</span>;
                          }

                          const matchedLanguages = languages.filter((item) =>
                            partner?.LanguagesServe?.includes(item.LangId),
                          );

                          const visibleLanguages = matchedLanguages.slice(0, 2);
                          const remainingCount =
                            matchedLanguages.length - visibleLanguages.length;

                          return (
                            <>
                              {visibleLanguages.map((item) => (
                                <span key={item.LangId}>{item.LangDesc}</span>
                              ))}

                              {remainingCount > 0 && (
                                <span> +{remainingCount} more</span>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </div>

                    <div className="agent-browse-saperator-line"></div>

                    <p className="agent-browse-description">
                      {partner.BusinessDescription}
                    </p>
                  </div>
                ))}

              {/* If nothing matches, show message */}
              {/* {allAgents.filter((partner) => {
                const matchesCity =
                  !currentCity ||
                  partner?.User?.Address?.CityId?.toLowerCase() ===
                    currentCity?.toLowerCase();
                const matchesState =
                  !currentState ||
                  partner?.User?.Address?.StateId?.toUpperCase() ===
                    currentState;

                return matchesCity && matchesState;
              }).length === 0 && (
                <div className="no-agents-message">
                  🚫 No agents found in your location
                </div>
              )} */}
            </div>
          </div>
        )}
        {selectedPartner && (
          <SelectedPartnerDetails
            partner={selectedPartner}
            religionOptions={religionOptions}
            casteOptions={casteOptions}
            communityOptions={communityOptions}
            languages={languages}
            onBack={() => setSelectedPartner(null)}
          />
        )}
      </div>
    </div>
  );
}

export default BrowseAgents;
